import { useState } from "react";
import { submitContact } from "../api/content";
import { ApiError } from "../api/client";

type Status = "idle" | "submitting" | "success" | "error";

interface FormState {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const EMPTY: FormState = { name: "", email: "", subject: "", message: "" };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Client-side validation mirroring the backend's request constraints. */
function validate(form: FormState): Partial<Record<keyof FormState, string>> {
  const errors: Partial<Record<keyof FormState, string>> = {};
  if (!form.name.trim()) errors.name = "Please enter your name.";
  if (!form.email.trim()) errors.email = "Please enter your email.";
  else if (!EMAIL_RE.test(form.email)) errors.email = "That doesn't look like a valid email.";
  if (form.message.trim().length < 10)
    errors.message = "Please write at least a sentence (10+ characters).";
  return errors;
}

/**
 * Contact page. Validates locally before posting to the public contact
 * endpoint, surfaces field-level and server errors, and confirms on success.
 */
export function ContactPage() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [status, setStatus] = useState<Status>("idle");
  const [serverError, setServerError] = useState<string>("");

  function update<K extends keyof FormState>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setStatus("submitting");
    setServerError("");
    try {
      await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || undefined,
        message: form.message.trim(),
      });
      setStatus("success");
      setForm(EMPTY);
    } catch (err) {
      setStatus("error");
      if (err instanceof ApiError && err.body?.fieldErrors?.length) {
        const mapped: Partial<Record<keyof FormState, string>> = {};
        for (const fe of err.body.fieldErrors) {
          mapped[fe.field as keyof FormState] = fe.message;
        }
        setErrors(mapped);
      } else {
        setServerError(
          err instanceof Error ? err.message : "Something went wrong sending your message.",
        );
      }
    }
  }

  return (
    <div className="page">
      <div className="container">
        <header className="page__head">
          <p className="eyebrow">Correspondence · DC</p>
          <h1 className="page__title">Contact / Sign the Book</h1>
          <p className="page__intro">
            Trip tips, dive buddies, corrections to the catalogue, or just to
            say hello — drop a note below and it lands in the inbox. No login, no
            newsletter, no catch.
          </p>
        </header>

        <div className="contact-direct">
          <p style={{ margin: 0 }}>
            <strong>Email:</strong>{" "}
            <a href="mailto:admin@csmckay.us">admin@csmckay.us</a>
          </p>
          <p style={{ margin: 0 }}>
            <strong>Phone:</strong> <a href="tel:+12028199900">202.819.9900</a>
          </p>
        </div>

        {status === "success" && (
          <p className="notice notice--ok" role="status">
            ✓ Message filed. Thanks for writing — I'll be in touch.
          </p>
        )}
        {status === "error" && serverError && (
          <p className="notice notice--err" role="alert">
            ✕ {serverError}
          </p>
        )}

        <form className="form" onSubmit={onSubmit} noValidate>
          <div className="field">
            <label htmlFor="name">Name</label>
            <input
              id="name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              aria-invalid={!!errors.name}
            />
            {errors.name && <span className="field__error">{errors.name}</span>}
          </div>

          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              aria-invalid={!!errors.email}
            />
            {errors.email && <span className="field__error">{errors.email}</span>}
          </div>

          <div className="field">
            <label htmlFor="subject">Subject (optional)</label>
            <input
              id="subject"
              value={form.subject}
              onChange={(e) => update("subject", e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              aria-invalid={!!errors.message}
            />
            {errors.message && <span className="field__error">{errors.message}</span>}
          </div>

          <div>
            <button className="btn" type="submit" disabled={status === "submitting"}>
              {status === "submitting" ? "Sending…" : "Send message"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
